import classnames from 'classnames';
import { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import FlowPoliciesList from "../../components/admin/flowpolicies/FlowPoliciesList";
import FlowPolicyModal from "../../components/admin/flowpolicies/FlowPolicyModal";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Select from "../../components/ui/Select";
import { AdminContext } from "../../context/AdminContext";
import handleToastError from "../../helpers/handleToastError";
import useApi from "../../hooks/useApi";
import { ActionType, IFlowExecution } from "../../types/ApiTypes";

const spaceTypes = [
  { label: "20", value: 20 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "55", value: 55 },
  { label: "56", value: 56 },
  { label: "70", value: 70 },
  { label: "75", value: 75 }
];

const actions: {label: string, value: ActionType}[] = [
  { label: "Vorm Erstellen", value: "pre_create" },
  { label: "Beim Erstellen", value: "create" },
  { label: "Beim Löschen", value: "delete" },
  { label: "Nachdem Antrag auf Besitzerwechsel", value: "owner_change" },
  { label: "Beim Fehlschlagen", value: "aborted" }
];

const FlowPolicies: FC = () => {
  const { updateFlowPolicyOrder, deleteFlowPolicy, toggleFlowPoliciesForAction, toggleFlowPolicy } = useApi();
  const admCtx = useContext(AdminContext);

  const [editModalOpen, setEditModalOpen] = useState(false);


  //Search policies
  const [selectedActionSearch, setSelectedActionSearch] = useState<ActionType>();
  const [selectedTypeSearch, setSelectedTypeSearch] = useState<number>();
  const [filteredPolicies, setFilteredPolicies] = useState<IFlowExecution[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<IFlowExecution>();
  const [isToggleLoading, setIsToggleLoading] = useState(false);

  const onActionSearchChange = (type: { label: string, value: ActionType }) => {
    setSelectedActionSearch(type.value);
  };

  const onSpaceTypeSearchChange = (type: { label: string, value: number }) => {
    setSelectedTypeSearch(type.value);
  };

  useEffect(() => {
    if (selectedTypeSearch && selectedActionSearch) {
      const policies = admCtx.state.policies.filter(x => x.action === selectedActionSearch && x.group_type === selectedTypeSearch);
      policies.sort((a, b) => {
        return a.order - b.order;
      });
      setFilteredPolicies(policies);
      return;
    }

    setFilteredPolicies([]);
  }, [selectedActionSearch, selectedTypeSearch, admCtx.state.policies]);

  const policiesForSelectedFilters = filteredPolicies;

  const areAllSelectedActionPoliciesDisabled = policiesForSelectedFilters.length > 0 && policiesForSelectedFilters.every((policy) => policy.isDisabled);

  const bulkToggleLabel = areAllSelectedActionPoliciesDisabled ? "Aktiviere alle policies" : "Deaktiviere alle policies";

  const onEdit = (policyId: number) => {
    const policy = filteredPolicies.find(x => x.id === policyId);
    console.log(policy, policyId, filteredPolicies);
    if(!policy) return;
    setSelectedPolicy(policy);
    setEditModalOpen(true);
  }

  const onOrderUp = async (flowId: number) => {
    const policies = filteredPolicies.concat();
    const currentPolicy = policies.find(x => x.id === flowId);
    if (!currentPolicy) return;
    const affectedPolicy = policies.find(x => x.order === currentPolicy.order + 1 && x.group_type === currentPolicy.group_type && x.action === currentPolicy.action);
    if (!affectedPolicy) return console.error("Affected policy not found");
    currentPolicy.order++;
    affectedPolicy.order--;
    await showToastAndUpdateOrder({
      [currentPolicy.id]: currentPolicy.order,
      [affectedPolicy.id]: affectedPolicy.order
    }, currentPolicy, affectedPolicy);
  };

  const onOrderDown = async (flowId: number) => {
    const policies = filteredPolicies.concat();
    const currentPolicy = policies.find(x => x.id === flowId);
    if (!currentPolicy) throw new Error("Unknown policy id");
    const affectedPolicy = policies.find(x => x.order === currentPolicy.order - 1 && x.group_type === currentPolicy.group_type && x.action === currentPolicy.action);
    if (!affectedPolicy) return console.error("Affected policy not found");
    currentPolicy.order--;
    affectedPolicy.order++;
    await showToastAndUpdateOrder({
      [currentPolicy.id]: currentPolicy.order,
      [affectedPolicy.id]: affectedPolicy.order
    }, currentPolicy, affectedPolicy);
  };

  const onDelete = async (flowId: number) => {
    await toast.promise(deleteFlowPolicy(flowId), {
      loading: "Die Policy wird gelöscht",
      success: (data) => {
        for (const singleFlow of data) {
          admCtx.dispatch({
            type: "UPDATE_POLICY",
            payload: { policyId: singleFlow.id, updatedPolicy: singleFlow }
          });
        }
        admCtx.dispatch({ type: "DELETE_POLICY", payload: flowId });
        return <span className="text-sm">Die Policy wurde gelöscht</span>;
      },
      error: (err) => handleToastError(err, "Die Policy konnte nicht gelöscht werden")
    });
  };

  const onTogglePolicies = async () => {
    if (!selectedActionSearch || !selectedTypeSearch || policiesForSelectedFilters.length === 0) return;

    const nextDisabledState = !areAllSelectedActionPoliciesDisabled;
    setIsToggleLoading(true);

    await toast.promise(toggleFlowPoliciesForAction(selectedActionSearch, selectedTypeSearch, nextDisabledState), {
      loading: nextDisabledState ? "Policies werden deaktiviert" : "Policies werden aktiviert",
      success: (updatedPolicies) => {
        for (const updatedPolicy of updatedPolicies) {
          admCtx.dispatch({
            type: "UPDATE_POLICY",
            payload: { policyId: updatedPolicy.id, updatedPolicy }
          });
        }

        return (
          <span className="text-sm">
            {nextDisabledState ? "Alle Policies wurden deaktiviert" : "Alle Policies wurden aktiviert"}
          </span>
        );
      },
      error: (err) => handleToastError(err, nextDisabledState ? "Policies konnten nicht deaktiviert werden" : "Policies konnten nicht aktiviert werden")
    });

    setIsToggleLoading(false);
  };

  const onTogglePolicy = async (policyId: number, isDisabled: boolean) => {
    await toast.promise(toggleFlowPolicy(policyId, isDisabled), {
      loading: isDisabled ? "Policy wird deaktiviert" : "Policy wird aktiviert",
      success: (updatedPolicy) => {
        admCtx.dispatch({
          type: "UPDATE_POLICY",
          payload: { policyId: updatedPolicy.id, updatedPolicy }
        });

        return <span className="text-sm">{isDisabled ? "Policy wurde deaktiviert" : "Policy wurde aktiviert"}</span>;
      },
      error: (err) => handleToastError(err, isDisabled ? "Policy konnte nicht deaktiviert werden" : "Policy konnte nicht aktiviert werden")
    });
  };

  const showToastAndUpdateOrder = async (mappedObj: {
    [p: string]: number
  }, currentPolicy: IFlowExecution, affectedPolicy: IFlowExecution) => {
    await toast.promise(updateFlowPolicyOrder(mappedObj), {
      loading: "Die Reihenfolge wird gewechselt",
      success: () => {
        admCtx.dispatch({
          type: "UPDATE_POLICY",
          payload: { policyId: currentPolicy.id, updatedPolicy: currentPolicy }
        });
        admCtx.dispatch({
          type: "UPDATE_POLICY",
          payload: { policyId: affectedPolicy.id, updatedPolicy: affectedPolicy }
        });
        return <span className="text-sm">Die Reihenfolge wurde gewechselt</span>;
      },
      error: (err) => handleToastError(err, "Die Reihenfolge konnte nicht gewechselt werden")
    });
  };

  return (
    <>
      <Card>
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label htmlFor="flow_name" className="font-semibold">Aktion</label>
            <Select options={actions} onChange={onActionSearchChange} placeholder="Wähle den Trigger" />
          </div>
          <div>
            <label htmlFor="flow_name" className="font-semibold">Spacetyp</label>
            <Select options={spaceTypes} onChange={onSpaceTypeSearchChange}
                            placeholder="Wähle den Spacetyp" />
          </div>
        </div>
        <div className="flex justify-end mb-3">
          <Button
            type="button"
            onClick={onTogglePolicies}
            disabled={!selectedActionSearch || !selectedTypeSearch || policiesForSelectedFilters.length === 0 || isToggleLoading}
            className={
              classnames(
                areAllSelectedActionPoliciesDisabled ? 'bg-success-darker hover:shadow-success-darker/20' : 'bg-error-default hover:shadow-error-default/20',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:pr-4',
              )
            }
          >
            {bulkToggleLabel}
          </Button>
        </div>
        <FlowPoliciesList onDelete={onDelete} onEdit={onEdit} onOrderUp={onOrderUp} onOrderDown={onOrderDown}
                          onTogglePolicy={onTogglePolicy} policies={filteredPolicies} />
        <div className="flex justify-end items-center mt-3">
          <FlowPolicyModal flowsList={admCtx.state.flows.map(x => ({label:x.name, value: x}))} actions={actions} spaceTypes={spaceTypes} flowPolicy={selectedPolicy} isOpen={editModalOpen} onClose={() => {
            setSelectedPolicy(undefined);
            setEditModalOpen(false);
          }}/>
        </div>
      </Card>
    </>
  );
};

export default FlowPolicies;